import { useMemo } from 'react';
import { Node, Edge } from '@xyflow/react';
import { DeviceData } from '../../DevicesTab';
import { MonitoringZoneDto, AssetVideoProcessorDto } from '@/lib/api/types.gen';
import { calculateZoneDimensions, calculateZonePositions, groupDevicesByZone } from '../utils/zoneCalculations';
import { createZoneNodes, createCameraNodes, createCameraDeviceDropHandler } from '../utils/nodeCreators';
import { calculateZoneBounds } from '../services/canvasConfig';
import { updateSpeaker } from '@/lib/api/monitoring/speakers';
import { updateRelay } from '@/lib/api/monitoring/relays';
import { useQueryClient } from '@tanstack/react-query';
import { LinkingMode } from '../types';

interface ZoneDimensions {
  width: number;
  height: number;
}

interface Site {
  monitoringZones?: MonitoringZoneDto[];
}


interface UseCanvasNodesProps {
  monitoredCameras: DeviceData[] | undefined;
  allSpeakers: DeviceData[];
  allRelays: DeviceData[];
  site: Site | undefined;
  siteId: string;
  companyId: string;
  hoveredCameraId: string | null;
  linkingMode: LinkingMode;
  deviceTypeFilters: {
    cameras: boolean;
    speakers: boolean;
    relays: boolean;
  };
  selectedZoneId: string;
  filterCameras: (cameras: DeviceData[]) => DeviceData[];
  handleStopMonitoring: (deviceId: string) => Promise<void>;
  handleEditGrids: (deviceId: string) => void;
  handleLinkToAllCameras: (deviceId: string, deviceType: string) => void;
  handleEnterLinkingMode: (deviceId: string, deviceType: 'speaker' | 'relay') => void;
  handleCameraClick: (cameraId: string) => void;
  handleZoneDropDevice: (deviceData: any, targetZoneId: string) => Promise<void>;
  handleRemoveEverythingFromZone: (zoneId: string) => void;
  setHoveredCameraId: (id: string | null) => void;
}

export const useCanvasNodes = ({
  monitoredCameras,
  allSpeakers,
  allRelays,
  site,
  siteId,
  companyId,
  hoveredCameraId,
  linkingMode,
  deviceTypeFilters,
  selectedZoneId,
  filterCameras,
  handleStopMonitoring,
  handleEditGrids,
  handleLinkToAllCameras,
  handleEnterLinkingMode,
  handleCameraClick,
  handleZoneDropDevice,
  handleRemoveEverythingFromZone,
  setHoveredCameraId,
}: UseCanvasNodesProps) => {
  const queryClient = useQueryClient();

  return useMemo(() => {
    const nodes: Node[] = [];
    
    const defaultZoneCameras = (monitoredCameras || []).filter(camera => {
      return !site?.monitoringZones?.some((zone: MonitoringZoneDto) => 
        zone.videoProcessors?.some((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId === camera.id)
      );
    });
    const defaultZoneDimensions = calculateZoneDimensions(defaultZoneCameras, allSpeakers, allRelays);
    
    interface ZoneWithDimensions {
      id: string;
      data: {
        zoneId: string;
        zoneName: string;
        isDefault?: boolean;
        zone?: MonitoringZoneDto;
        zoneDimensions?: ZoneDimensions;
      };
      dimensions: ZoneDimensions;
    }
    
    const allZones: ZoneWithDimensions[] = [];
    
    // Only show the selected zone
    if (selectedZoneId === 'default') {
      allZones.push({
        id: 'default-zone',
        data: {
          zoneId: 'default',
          zoneName: 'Default Zone',
          isDefault: true,
        },
        dimensions: defaultZoneDimensions,
      });
    } else if (site?.monitoringZones) {
      const selectedZone = site.monitoringZones.find(zone => zone.id === selectedZoneId);
      if (selectedZone) {
        const zoneCameras = (monitoredCameras || []).filter(camera => 
          selectedZone.videoProcessors?.some((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId === camera.id)
        );
        
        const zoneDimensions = calculateZoneDimensions(zoneCameras, allSpeakers, allRelays);
        
        allZones.push({
          id: `zone-${selectedZone.id}`,
          data: {
            zoneId: selectedZone.id!,
            zoneName: selectedZone.description || 'Zone',
            isDefault: false,
            zone: selectedZone,
            zoneDimensions,
          },
          dimensions: zoneDimensions,
        });
      }
    }
    
    const positions = calculateZonePositions(allZones);
    
    // Calculate zone bounds and minimum zoom
    const zoneBounds = calculateZoneBounds(allZones, positions);
    const padding = 100; // Add some padding around zones
    const topPadding = 200; // Extra buffer on top for search controls
    const canvasWidth = window.innerWidth || 1200;
    const canvasHeight = window.innerHeight || 800;
    
    const contentWidth = zoneBounds.maxX - zoneBounds.minX + (2 * padding);
    const contentHeight = zoneBounds.maxY - zoneBounds.minY + padding + topPadding;
    
    const zoomX = canvasWidth / contentWidth;
    const zoomY = canvasHeight / contentHeight;
    const calculatedMinZoom = Math.min(zoomX, zoomY, 1); // Don't go above 1x zoom
    const minZoom = Math.max(calculatedMinZoom, 0.1); // Don't go below 0.1x zoom
    
    // translateExtent removed - now using dynamic pan constraints
    
    if (monitoredCameras && allZones.length > 0) {
      const { camerasByZone, speakersByZone, relaysByZone, defaultCameras } = groupDevicesByZone(
        monitoredCameras, 
        allSpeakers, 
        allRelays, 
        site
      );
      
      // Get cameras for the selected zone first
      let zoneCameras: DeviceData[] = [];
      if (selectedZoneId === 'default') {
        zoneCameras = defaultCameras;
      } else {
        zoneCameras = camerasByZone.get(selectedZoneId) || [];
      }
      
      // Create zone nodes using zone-specific camera count
      const zoneNodes = createZoneNodes({
        allZones,
        positions,
        companyId,
        siteId,
        onDeviceDrop: handleZoneDropDevice,
        onRemoveEverything: handleRemoveEverythingFromZone,
        totalCameraCount: zoneCameras.length,
      });
      
      nodes.push(...zoneNodes);
      
      const cameraDeviceDropHandler = createCameraDeviceDropHandler(queryClient, companyId, siteId, monitoredCameras);
      const selectedZone = allZones[0]; // Only one zone in the array now
      const zonePosition = positions[0]; // Position for the selected zone
      
      const filteredCameras = filterCameras(zoneCameras);
      
      // Create camera nodes for the selected zone
      const cameraNodes = createCameraNodes({
        cameras: filteredCameras,
        zonePosition: zonePosition,
        nodeData: {
          siteId,
          companyId,
          onStopMonitoring: handleStopMonitoring,
          onEditGrids: handleEditGrids,
          hoveredCameraId,
          onMouseEnter: setHoveredCameraId,
          onMouseLeave: () => setHoveredCameraId(null),
          onCameraClick: linkingMode.active ? handleCameraClick : undefined,
          isLinkingMode: linkingMode.active,
          linkingMode,
          onDeviceDrop: cameraDeviceDropHandler,
          allSpeakers,
          allRelays,
        },
        totalCameraCount: zoneCameras.length
      });
      nodes.push(...cameraNodes);
      
      // Position speakers in the selected zone only
      const selectedZoneSpeakers = selectedZoneId === 'default' 
        ? speakersByZone.get('default') || []
        : speakersByZone.get(selectedZoneId) || [];
      
      if (selectedZoneSpeakers.length > 0) {
        // Filter speakers based on type filter
        const filteredSpeakers = deviceTypeFilters.speakers ? selectedZoneSpeakers : [];
            
        filteredSpeakers.forEach((speaker: DeviceData, speakerIndex: number) => {
          const speakerX = zonePosition.x + 20 + (speakerIndex * 180);
          const speakerY = zonePosition.y + 270;
              
              nodes.push({
                id: `speaker-${speaker.id}`,
                type: 'speaker',
                position: { x: speakerX, y: speakerY },
                data: {
                  deviceId: speaker.id,
                  deviceName: speaker.name,
                  assetName: speaker.assetName,
                  nvrName: speaker.nvrName,
                  nvrId: speaker.nvrId,
                  relatedComponentGroups: speaker.relatedComponentGroups,
                  associatedCameraIds: speaker.associatedCameraIds,
                  associatedCameraId: speaker.associatedCameraId,
                  onStopMonitoring: handleStopMonitoring,
                  onEditGrids: handleEditGrids,
                  hoveredCameraId,
                  onLinkToAllCameras: handleLinkToAllCameras,
                  onEnterLinkingMode: handleEnterLinkingMode,
                  isCurrentLinkingDevice: linkingMode.active && linkingMode.deviceId === speaker.id,
                  onMouseEnter: setHoveredCameraId,
                  onMouseLeave: () => setHoveredCameraId(null),
                  onRemoveFromZone: async (deviceId: string) => {
                    await updateSpeaker({
                      id: deviceId,
                      name: speaker.name,
                      componentGroupId: speaker.componentGroupId || deviceId,
                      relatedComponentGroups: []
                    });
                    
                    // Invalidate queries to trigger UI update
                    queryClient.invalidateQueries({ 
                      queryKey: ['siteDevices', { companyId, projectId: siteId }],
                      refetchType: 'all'
                    });
                  },
                },
                draggable: false,
                selectable: true,
                zIndex: 500,
              });
        });
      }
      
      // Position relays in the selected zone only
      const selectedZoneRelays = selectedZoneId === 'default' 
        ? relaysByZone.get('default') || []
        : relaysByZone.get(selectedZoneId) || [];
      
      if (selectedZoneRelays.length > 0) {
        // Filter relays based on type filter
        const filteredRelays = deviceTypeFilters.relays ? selectedZoneRelays : [];
        
        filteredRelays.forEach((relay: DeviceData, relayIndex: number) => {
          const relayX = zonePosition.x + 20 + (relayIndex * 180);
          const relayY = zonePosition.y + 400;
          
          nodes.push({
            id: `relay-${relay.id}`,
            type: 'relay',
            position: { x: relayX, y: relayY },
            data: {
              deviceId: relay.id,
              deviceName: relay.name,
              assetName: relay.assetName,
              nvrName: relay.nvrName,
              nvrId: relay.nvrId,
              relatedComponentGroups: relay.relatedComponentGroups,
              associatedCameraIds: relay.associatedCameraIds,
              associatedCameraId: relay.associatedCameraId,
              onStopMonitoring: handleStopMonitoring,
              onEditGrids: handleEditGrids,
              hoveredCameraId,
              onLinkToAllCameras: handleLinkToAllCameras,
              onEnterLinkingMode: handleEnterLinkingMode,
              isCurrentLinkingDevice: linkingMode.active && linkingMode.deviceId === relay.id,
              onMouseEnter: setHoveredCameraId,
              onMouseLeave: () => setHoveredCameraId(null),
              onRemoveFromZone: async (deviceId: string) => {
                await updateRelay({
                  id: deviceId,
                  name: relay.name,
                  componentGroupId: relay.componentGroupId || deviceId,
                  relatedComponentGroups: []
                });
                
                // Invalidate queries to trigger UI update
                queryClient.invalidateQueries({ 
                  queryKey: ['siteDevices', { companyId, projectId: siteId }],
                  refetchType: 'all'
                });
              },
            },
            draggable: false,
            selectable: true,
            zIndex: 500,
          });
        });
      }
    }
    
    // Simple initial viewport - no hard constraints
    const initialZoom = Math.max(minZoom, 0.8);
    return { nodes, minZoom, initialViewport: { x: 0, y: 0, zoom: initialZoom } };
  }, [
    monitoredCameras?.length,
    site?.monitoringZones,
    selectedZoneId,
    allSpeakers,
    allRelays,
    hoveredCameraId,
    linkingMode.active,
    linkingMode.deviceId,
    deviceTypeFilters.cameras,
    deviceTypeFilters.speakers,
    deviceTypeFilters.relays,
  ]);
};